import { STREAM_STYLE_LABELS } from './stream-themes';
import { CheckCircle2, RefreshCw, Save, Settings2, ShieldAlert, Trash2, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyStreamProfile,
  deleteStreamProfile,
  loadStreamConfiguration,
  runWelcomeRehearsal,
  saveSafetyModes,
  saveStreamProfile,
  type StreamConfiguration,
  type StreamProfile,
  type StreamProfileDraft,
} from './stream-configuration-api';
import type { StreamEffectStyle } from './stream-api';
import './stream-configuration.css';

const STYLE_LABELS = STREAM_STYLE_LABELS;

function emptyProfile(): StreamProfileDraft {
  return {
    id: null,
    name: '',
    style: 'black-yellow',
    welcomeEnabled: true,
    alertsEnabled: true,
    wamEnabled: true,
    commandSet: [],
    audioSettings: {},
    moduleSettings: {},
    safeModeDefaults: {},
  };
}

function profileDraft(profile: StreamProfile): StreamProfileDraft {
  return {
    ...profile,
    commandSet: [...profile.commandSet],
    audioSettings: { ...profile.audioSettings },
    moduleSettings: { ...profile.moduleSettings },
    safeModeDefaults: { ...profile.safeModeDefaults },
  };
}

export function StreamConfigurationSection({ role }: { role: 'owner' | 'operator' }) {
  const [configuration, setConfiguration] = useState<StreamConfiguration | null>(null);
  const [rehearsal, setRehearsal] = useState(false);
  const [safeMode, setSafeMode] = useState(false);
  const [modeDirty, setModeDirty] = useState(false);
  const modeDirtyRef = useRef(false);
  const [rehearsalLogin, setRehearsalLogin] = useState('');
  const [draft, setDraft] = useState<StreamProfileDraft>(emptyProfile);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const setModeDirtyState = (value: boolean) => {
    modeDirtyRef.current = value;
    setModeDirty(value);
  };

  const refresh = useCallback(async (resetModes = false) => {
    try {
      const next = await loadStreamConfiguration();
      setConfiguration(next);
      if (resetModes || !modeDirtyRef.current) {
        setRehearsal(next.rehearsalEnabled);
        setSafeMode(next.safeModeEnabled);
        setModeDirtyState(false);
      }
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream profiles could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  function beginNewProfile() {
    setDraft(emptyProfile());
    setMessage('');
  }

  function editProfile(profile: StreamProfile) {
    setDraft(profileDraft(profile));
    setMessage('');
  }

  async function saveModes() {
    if (!modeDirty || !configuration) return;
    const changes: string[] = [];
    if (rehearsal !== configuration.rehearsalEnabled) changes.push(`Rehearsal ${rehearsal ? 'ON' : 'OFF'}`);
    if (safeMode !== configuration.safeModeEnabled) changes.push(`Safe Mode ${safeMode ? 'ON' : 'OFF'}`);
    if (!changes.length) return;
    if (!window.confirm(`Save stream safety modes?\n\n${changes.join('\n')}\n\nSafe Mode suppresses non-essential live features while enabled.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await saveSafetyModes(rehearsal, safeMode);
      setModeDirtyState(false);
      await refresh(true);
      setMessage('Stream safety modes saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream safety modes could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function rehearseWelcome(kind: 'welcome-fallback' | 'welcome-personalised') {
    if (!configuration?.rehearsalEnabled || configuration.safeModeEnabled || modeDirty) return;
    const login = rehearsalLogin.trim().replace(/^@+/, '');
    if (kind === 'welcome-personalised' && !login) {
      setMessage('Enter a Twitch login to rehearse a personalised welcome.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await runWelcomeRehearsal(kind, login);
      setMessage(kind === 'welcome-fallback' ? 'Fallback welcome audio rehearsal queued.' : `Personalised welcome audio rehearsal queued for @${login}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Welcome audio rehearsal could not be queued.');
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    if (!draft.name.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      const editing = Boolean(draft.id);
      await saveStreamProfile(draft);
      await refresh(false);
      setDraft(emptyProfile());
      setMessage(editing ? 'Stream profile updated.' : 'Stream profile created.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream profile could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function applyProfile(profile: StreamProfile) {
    if (configuration?.activeProfileId === profile.id) return;
    if (!window.confirm(`Apply “${profile.name}” as the active stream profile?\n\nThis updates the FFSControl stream profile and presentation settings.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await applyStreamProfile(profile.id);
      await refresh(false);
      setMessage(`${profile.name} applied.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream profile could not be applied.');
    } finally {
      setBusy(false);
    }
  }

  async function removeProfile(profile: StreamProfile) {
    if (configuration?.activeProfileId === profile.id) return;
    if (!window.confirm(`Delete stream profile “${profile.name}”?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await deleteStreamProfile(profile.id);
      if (draft.id === profile.id) setDraft(emptyProfile());
      await refresh(false);
      setMessage(`${profile.name} deleted.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Stream profile could not be deleted.');
    } finally {
      setBusy(false);
    }
  }

  async function manualRefresh() {
    if (modeDirty && !window.confirm('Discard unsaved Rehearsal / Safe Mode changes and reload stream setup?')) return;
    setModeDirtyState(false);
    await refresh(true);
  }

  const rehearsalAvailable = configuration?.rehearsalEnabled === true && configuration.safeModeEnabled === false && !modeDirty;

  return (
    <section className="ffs-stream-configuration-section" aria-label="Advanced stream setup">
      {message ? <div className="ffs-workspace-message" aria-live="polite">{message}</div> : null}
      <details className="ffs-workspace-card ffs-stream-config-disclosure">
        <summary>
          <div><small>ADVANCED STREAM SETUP</small><strong>Profiles &amp; safety modes</strong><span>Rehearsal, Safe Mode and reusable stream profiles.</span></div>
          <Settings2 size={18} />
        </summary>

        <div className="ffs-stream-config-body">
          <div className="ffs-stream-config-toolbar">
            <div><span>Shared stream controls · {role === 'owner' ? 'Owner' : 'Lead Moderator'}</span>{configuration?.activeProfileId ? <small>One profile is currently active.</small> : <small>No active profile selected.</small>}</div>
            <button type="button" className="ffs-button secondary" disabled={busy || loading} onClick={() => void manualRefresh()}><RefreshCw size={14} /> Reload</button>
          </div>

          {loading && !configuration ? <div className="ffs-empty-state">Loading advanced stream setup…</div> : configuration ? (
            <>
              <section className="ffs-stream-mode-panel">
                <div className="heading"><div><small>SAFETY MODES</small><strong>Runtime guardrails</strong></div><ShieldAlert size={18} /></div>
                <div className="ffs-stream-mode-choices">
                  <label className={rehearsal ? 'selected' : ''}><input type="checkbox" checked={rehearsal} disabled={busy} onChange={(event) => { setRehearsal(event.target.checked); setModeDirtyState(true); }} /><div><strong>Rehearsal</strong><span>Use rehearsal-aware behaviour for safe testing.</span></div></label>
                  <label className={safeMode ? 'selected' : ''}><input type="checkbox" checked={safeMode} disabled={busy} onChange={(event) => { setSafeMode(event.target.checked); setModeDirtyState(true); }} /><div><strong>Safe Mode</strong><span>Suppress non-essential live features when you need a reduced-risk setup.</span></div></label>
                  <button type="button" className="ffs-button primary" disabled={busy || !modeDirty} onClick={() => void saveModes()}><Save size={14} /> Save Modes</button>
                </div>

                <div className="ffs-stream-rehearsal-tools">
                  <div className="heading"><div><small>REHEARSAL AUDIO</small><strong>Welcome audio tests</strong><span>Uses the native shared-audio path without creating a real viewer welcome.</span></div><Volume2 size={17} /></div>
                  <div className="ffs-stream-rehearsal-actions">
                    <button type="button" className="ffs-button secondary" disabled={busy || !rehearsalAvailable} onClick={() => void rehearseWelcome('welcome-fallback')}>Rehearse Fallback Welcome</button>
                    <label><span>Twitch login</span><input value={rehearsalLogin} disabled={busy || !rehearsalAvailable} onChange={(event) => setRehearsalLogin(event.target.value)} placeholder="viewer_login" /></label>
                    <button type="button" className="ffs-button secondary" disabled={busy || !rehearsalAvailable || !rehearsalLogin.trim()} onClick={() => void rehearseWelcome('welcome-personalised')}>Rehearse Personalised Welcome</button>
                  </div>
                  <small className="ffs-stream-rehearsal-state">{modeDirty ? 'Save Rehearsal / Safe Mode changes before running a test.' : configuration.safeModeEnabled ? 'Safe Mode blocks rehearsal actions.' : configuration.rehearsalEnabled ? 'Rehearsal mode is active.' : 'Enable and save Rehearsal mode to use these tests.'}</small>
                </div>
              </section>

              <section className="ffs-stream-profile-workspace">
                <div className="ffs-stream-profile-list">
                  <div className="heading"><div><small>REUSABLE SETUPS</small><strong>Stream profiles</strong></div><button type="button" className="ffs-button secondary" disabled={busy} onClick={beginNewProfile}>New Profile</button></div>
                  <div className="list">
                    {configuration.profiles.length === 0 ? <div className="ffs-empty-state">No stream profiles configured.</div> : configuration.profiles.map((profile) => {
                      const active = profile.id === configuration.activeProfileId;
                      return <article className={active ? 'active' : ''} key={profile.id}>
                        <div><strong>{profile.name}</strong><span>{STYLE_LABELS[profile.style]}{active ? ' · ACTIVE' : ''}</span><small>{profile.welcomeEnabled ? 'Welcomes' : 'No welcomes'} · {profile.alertsEnabled ? 'Alerts' : 'No alerts'} · {profile.wamEnabled ? 'WAM' : 'No WAM'}</small></div>
                        <div className="actions"><button type="button" className="ffs-button secondary" disabled={busy} onClick={() => editProfile(profile)}>Edit</button><button type="button" className={`ffs-button ${active ? 'secondary' : 'primary'}`} disabled={busy || active} onClick={() => void applyProfile(profile)}>{active ? <><CheckCircle2 size={13} /> Applied</> : 'Apply'}</button><button type="button" className="ffs-button danger" disabled={busy || active} onClick={() => void removeProfile(profile)}><Trash2 size={13} /> Delete</button></div>
                      </article>;
                    })}
                  </div>
                </div>

                <div className="ffs-stream-profile-editor">
                  <div className="heading"><div><small>{draft.id ? 'EDIT PROFILE' : 'NEW PROFILE'}</small><strong>{draft.id ? draft.name : 'Profile details'}</strong></div></div>
                  <label><span>Name</span><input maxLength={100} value={draft.name} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Friday WAM" /></label>
                  <label><span>Stream style</span><select value={draft.style} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, style: event.target.value as StreamEffectStyle }))}>{Object.entries(STYLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  <div className="ffs-stream-profile-checks">
                    <label><input type="checkbox" checked={draft.welcomeEnabled} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, welcomeEnabled: event.target.checked }))} /><span>Welcomes</span></label>
                    <label><input type="checkbox" checked={draft.alertsEnabled} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, alertsEnabled: event.target.checked }))} /><span>Alerts</span></label>
                    <label><input type="checkbox" checked={draft.wamEnabled} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, wamEnabled: event.target.checked }))} /><span>WAM</span></label>
                  </div>
                  <p>Editing these visible settings preserves the profile’s command, audio, module and Safe Mode metadata.</p>
                  <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || !draft.name.trim()} onClick={() => void saveProfile()}>{draft.id ? 'Update Profile' : 'Create Profile'}</button>{draft.id ? <button type="button" className="ffs-button secondary" disabled={busy} onClick={beginNewProfile}>Cancel</button> : null}</div>
                </div>
              </section>
            </>
          ) : <div className="ffs-empty-state">Advanced stream setup is unavailable. Use Reload to retry.</div>}
        </div>
      </details>
    </section>
  );
}
